import { forwardRef, useState, type JSX } from 'react';

import { Input } from '../../primitives/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
} from '../../primitives/input-group';
import { LoadingAddon } from '../../primitives/loading-addon';
import { cn } from '../../utils/cn';

interface InputIdProps extends React.ComponentProps<typeof Input> {
  button?: {
    disabled?: boolean;
    icon?: JSX.Element;
    id?: string;
    label?: string;
    onClick?: VoidFunction;
    text?: string;
  };
  counter?: {
    enable: boolean;
  };
  isLoading?: boolean;
  type?: 'guid';
}

const InputId = forwardRef<HTMLInputElement, InputIdProps>(
  (
    {
      button,
      className,
      counter,
      disabled = false,
      isLoading = false,
      readOnly = false,
      sizesx,
      type = 'guid',
      value: tmpValue,
      onChange,
      ...props
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = useState('');
    const value = tmpValue === undefined ? internalValue : tmpValue;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e);
      } else {
        setInternalValue(e.target.value);
      }
    };

    const handleGenerate = () => {
      const generatedId = crypto?.randomUUID() ?? '';

      if (onChange) {
        onChange({
          target: { value: generatedId } as HTMLInputElement,
        } as React.ChangeEvent<HTMLInputElement>);
      } else {
        setInternalValue(generatedId);
      }
    };

    const currentLength = typeof value === 'string' ? value.length : 0;

    return (
      <InputGroup disabled={disabled || isLoading}>
        <Input
          autoComplete="off"
          className={cn(
            'border-0 shadow-none ring-0 focus-within:ring-0 focus-visible:ring-0',
            className,
          )}
          data-slot="input-group-control"
          disabled={disabled || isLoading}
          readOnly={readOnly}
          ref={ref}
          sizesx={sizesx}
          value={value}
          onChange={handleChange}
          {...props}
        />

        <InputGroupAddon align="block-end" className="flex-wrap justify-end gap-4">
          {counter?.enable && (
            <InputGroupText>
              {currentLength} {currentLength === 1 ? 'carácter' : 'caracteres'}
            </InputGroupText>
          )}
          <InputGroupButton
            className="flex-none"
            title={button?.text ?? button?.label ?? 'Auto generar id'}
            disabled={disabled || isLoading || button?.disabled || (readOnly && !button?.onClick)}
            id={button?.id ?? (props?.id ? `${props?.id}-button` : undefined)}
            size="xs"
            variant="secondary"
            onClick={button?.onClick ?? handleGenerate}
          >
            {button?.icon}
            {button?.text ?? button?.label ?? 'Auto generar'}
          </InputGroupButton>
        </InputGroupAddon>
        {isLoading && <LoadingAddon />}
      </InputGroup>
    );
  },
);

InputId.displayName = 'InputId';

export { InputId };
